import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText, Section, StatusText, TextField } from "../../ui";
import { useRefreshWorkspace } from "./refresh";

export function MembersSection({
  workspaceId,
  isOwner,
  personal,
  myUid,
}: {
  workspaceId: string;
  isOwner: boolean;
  personal: boolean;
  myUid: string;
}) {
  const trpc = useTRPC();
  const refresh = useRefreshWorkspace(workspaceId);
  const members = useQuery(trpc.workspaces.members.queryOptions({ workspaceId }));
  const remove = useMutation(trpc.workspaces.removeMember.mutationOptions({ onSuccess: () => refresh() }));

  return (
    <Section
      title="Members"
      {...(personal ? { description: "A Personal workspace is yours alone." } : {})}
    >
      {members.isPending ? <p className="text-sm text-muted">Loading…</p> : null}
      {members.isError ? <ErrorText>{errorMessage(members.error)}</ErrorText> : null}
      {members.isSuccess ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted">
              <tr>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Name
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Email
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Role
                </th>
                {isOwner ? (
                  <th scope="col" className="py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {members.data.map((m) => {
                const label = m.name === null ? m.uid : m.name;
                return (
                  <tr key={m.uid} className="border-t border-border">
                    <td className="py-2 pr-4 font-medium text-text">
                      {label}
                      {m.uid === myUid ? <span className="text-muted"> (you)</span> : null}
                    </td>
                    <td className="py-2 pr-4 text-muted">{m.email === null ? "—" : m.email}</td>
                    <td className="py-2 pr-4 text-muted">{m.role === "owner" ? "Owner" : "Member"}</td>
                    {isOwner ? (
                      <td className="py-2 text-right">
                        {m.role === "owner" ? null : (
                          <Button
                            variant="danger"
                            disabled={remove.isPending}
                            aria-label={`Remove ${label}`}
                            onClick={() => remove.mutate({ workspaceId, uid: m.uid })}
                          >
                            Remove
                          </Button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {remove.isError ? <ErrorText>{errorMessage(remove.error)}</ErrorText> : null}
      {isOwner && !personal ? <AddMemberForm workspaceId={workspaceId} /> : null}
    </Section>
  );
}

function AddMemberForm({ workspaceId }: { workspaceId: string }) {
  const trpc = useTRPC();
  const refresh = useRefreshWorkspace(workspaceId);
  const [email, setEmail] = useState("");
  const [added, setAdded] = useState<string | null>(null);
  const add = useMutation(
    trpc.workspaces.addMember.mutationOptions({
      onSuccess: async (_data, input) => {
        setAdded(input.email);
        setEmail("");
        await refresh();
      },
    }),
  );

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setAdded(null);
    add.mutate({ workspaceId, email: email.trim() });
  }

  return (
    <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-2 border-t border-border pt-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <TextField
          label="Add member by email"
          hint="They must have signed in to KitWork+ at least once."
          type="email"
          name="email"
          autoComplete="off"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" disabled={add.isPending}>
          {add.isPending ? "Adding…" : "Add member"}
        </Button>
      </div>
      <div className="min-h-5">
        {add.isError ? <ErrorText>{errorMessage(add.error)}</ErrorText> : null}
        {added === null ? null : <StatusText>Added {added}.</StatusText>}
      </div>
    </form>
  );
}
